import { executiveState, WITHHELD_PILLAR_STRENGTH } from '../utils/executiveState';
import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, AlertTriangle, Info, AlertCircle } from 'lucide-react';
import Disclaimer from './Disclaimer';
import NarrativeReadbackBadge, { NarrativeReadbackShape } from './NarrativeReadbackBadge';
import EvidencePassportFooter, { EvidencePassportShape } from './EvidencePassportFooter';
import CompositeBridgePanel, { CompositeBridgeShape } from './CompositeBridgePanel';
import NarrativeProvenanceChip from './NarrativeProvenanceChip';
import TypedMetricRegistryPanel, { TypedMetricRegistryShape } from './TypedMetricRegistryPanel';
import { gradeDisplay } from '../utils/gradeDisplay';

// Institutional analysis contract (matches backend institutional_analysis)
export interface InstitutionalAnalysisType {
  composite: {
    score: number;
    grade: string;
    method: string;
    weights: Record<string, number>;
    confidence: number;
    data_quality: number;
    // Deferred backlog 2026-08-22: single confidence-effective value from the backend so the
    // frontend can't accidentally publish the uncapped raw. Optional for backwards compat with
    // older payloads — frontend falls back to Math.min(confidence, data_quality) when absent.
    confidence_effective?: number;
    confidence_capped_by_dq?: boolean;
  };
  categories: Array<{ id: string; score: number; delta_30d?: number; trend_30d?: string; confidence?: number; data_quality?: number }>;
  contribution_pp: Array<{ id: string; value: number }>;
  risk_panel?: {
    realized_vol_30d_pct?: number;
    var_95_1d_pct?: number;
    cvar_95_1d_pct?: number;
    var_99_1d_pct?: number;
    max_drawdown_1y_pct?: number;
    max_drawdown_90d_pct?: number;
    method?: string;
    slippage_10m_usd_bps?: number;
  };
  signal_health?: Record<string, number>;
  // AUP §9: no action, no allocation_range_pct, no posture `reason` — the API sends none of them.
  recommendation?: {
    risk_level: string;
    constraints?: string[];
  };
  provenance?: {
    model_version: string;
    framework_ref?: string;
    feature_store_snapshot: string;
    sources?: string[];
    report_id: string;
  };
}

interface AISummaryProps {
  asset: string;
  /** Secondary narrative layer rendered inside the Executive Summary card (Orchestrator brief, SR-364). */
  afterSummary?: React.ReactNode;
  metrics: any;
  overallRating: number | null;
  grade: string | null;
  onViewDetailedAnalysis?: () => void; // legacy — the action now lives in the header
  aiSummaryText?: string;
  institutionalAnalysis?: InstitutionalAnalysisType | null;
  narrativeReadback?: NarrativeReadbackShape | null;
  /** SR-147 canonical enum from the core: DETERMINISTIC_TEMPLATE | LLM_RECONCILED | LLM_SUPPRESSED. */
  narrativeOrigin?: string | null;
  evidencePassport?: EvidencePassportShape | null;
  compositeBridge?: CompositeBridgeShape | null;
  typedMetricRegistry?: TypedMetricRegistryShape | null;
}

const CATEGORY_LABELS: Record<string, string> = {
  fundamentals: 'Fundamentals',
  on_chain: 'Network Activity',
  market_sentiment: 'Market Quality',
  security: 'Security & Resilience',
  regulatory: 'Regulatory Scope',
  esg: 'Environmental',
};

const AISummary: React.FC<AISummaryProps> = ({ asset, metrics, overallRating, grade, aiSummaryText, institutionalAnalysis, narrativeReadback, narrativeOrigin, evidencePassport, compositeBridge, typedMetricRegistry, afterSummary }) => {
  // SR-276: the class framework withheld the composite — no score, no grade, no drivers.
  const compWithheld: boolean = Boolean((compositeBridge as any)?.composite_withheld || metrics?.composite_withheld);
  const compFlags: string[] = (compositeBridge as any)?.composite_withheld_flags ?? metrics?.composite_withheld_flags ?? [];
  const compCovRaw = (compositeBridge as any)?.scoreable_nominal_coverage ?? metrics?.scoreable_nominal_coverage;
  const compCovText: string = typeof compCovRaw === 'number' ? `${(compCovRaw * 100).toFixed(0)}%` : '—';
  React.useEffect(() => {
    if (metrics) {
      // Executive summary (deterministic, composed from the analytics core)
      console.log('Asset:', asset.toUpperCase(), '| Institutional analysis:', institutionalAnalysis ? 'yes' : 'no');
    }
  }, [asset, metrics, institutionalAnalysis]);

  if (!metrics) return null;

  // Single source of truth: ONLY the API/core category scores (metrics.rating_*). No JS-side
  // heuristics, no hardcoded 70/75 — a pillar the core did not measure renders N/A and is
  // excluded from strongest/watch selection (never fabricated).
  const nn = (v: any): number | null => (typeof v === 'number' && isFinite(v) ? v : null);
  const fundamentalsScore = nn(metrics.rating_fundamentals);
  const onChainScore = nn(metrics.rating_onchain);
  const marketScore = nn(metrics.rating_market);
  const securityScore = nn(metrics.rating_security);
  const regulatoryScore = nn(metrics.rating_regulatory);
  const esgScore = nn(metrics.rating_esg);

  type Factor = { name: string; score: number | null; id: string; change: string };
  const factors: Factor[] = [
    { name: 'Fundamentals', score: fundamentalsScore, id: 'fundamentals', change: '' },
    { name: 'Network Activity', score: onChainScore, id: 'on_chain', change: '' },
    { name: (metrics as any)?.market_model === 'market_quality_v2_institutional_tradability' ? 'Market Quality' : ((metrics as any)?.market_methodology_id ? 'Market Quality (legacy v1)' : 'Market Quality'), score: marketScore, id: 'market_sentiment', change: '' },
    { name: 'Security & Resilience', score: securityScore, id: 'security', change: '' },
    { name: 'Regulatory Scope', score: regulatoryScore, id: 'regulatory', change: '' },
    { name: 'Environmental', score: esgScore, id: 'esg', change: '' }
  ];

  type ScoredFactor = { name: string; score: number; id: string; change: string };
  const scored = factors.filter((f): f is ScoredFactor => f.score != null);
  const EMPTY: Factor = { name: '—', score: null, id: '', change: '' };
  // Report parity: ESG is EXCLUDED from the "strongest pillar" selection. For digital assets ESG is
  // E-only — the S and G sub-dimensions are structurally unmeasured (WITHHELD) — so its score does not
  // represent the full pillar and must not be crowned strongest. The institutional report applies the
  // same exclusion (→ Security, not ESG). ESG still appears in the pillar table; only the "strongest"
  // headline drops it. Falls back to the full set if ESG were somehow the only scored pillar.
  // Pre-revenue audit: "strongest driver" and "primary watch" MUST be ranked by CONTRIBUTION to the
  // composite (percentage points), the SAME definition the composite bridge, the Dossier View and the
  // narrative-readback selector use — so every surface names the same pillar. Ranking by raw SCORE (the
  // old behaviour) made the summary call Security the "strength" (highest score, 98.8) while the
  // bridge's biggest driver was Fundamentals (+8.76pp), and the readback flagged the disagreement.
  // Contribution = (score − 50) × normalized_weight, so a high-weight mid-score pillar (Fundamentals,
  // 0.30) can out-drive a low-weight top-score pillar (ESG, 0.05). Falls back to score when the
  // contribution vector is absent (older payloads).
  // Pre-revenue audit P0-7/P0-11: contributions, "strongest driver" and "primary watch" ALL read
  // from the composite bridge — the ONE withheld-aware, renormalized source the bridge table itself
  // shows. The previous read of institutional_analysis.contribution_pp only dropped a withheld
  // ON-CHAIN pillar (not a withheld Security/ESG), so on TRX (Security withheld) the summary showed
  // "Security +3.75pp" while the bridge had Security WITHHELD, and could crown a withheld pillar. A
  // WITHHELD pillar contributes nothing and can NEVER be strongest/watch.
  const FACTOR_TO_BRIDGE: Record<string, string> = {
    fundamentals: 'fundamentals', on_chain: 'onchain', market_sentiment: 'market',
    security: 'security', regulatory: 'regulatory', esg: 'esg',
  };
  const bridgePillars = (compositeBridge?.pillars ?? []) as Array<{ id: string; label: string; score: number; contribution_pp: number; status: string }>;
  const haveBridge = bridgePillars.length > 0;
  const bridgeOf = (fid: string) => bridgePillars.find((p) => p.id === FACTOR_TO_BRIDGE[fid]);
  const isWithheld = (fid: string) => bridgeOf(fid)?.status === 'WITHHELD';
  const contribById = new Map<string, number>((institutionalAnalysis?.contribution_pp ?? []).map((c) => [c.id, c.value]));
  // Contribution for a factor: from the bridge (renormalized, null when withheld); falls back to the
  // institutional_analysis vector only when no bridge is present (older payloads).
  const contribOf = (fid: string): number | null => {
    if (haveBridge) {
      const b = bridgeOf(fid);
      return b && typeof b.contribution_pp === 'number' && b.status !== 'WITHHELD' ? b.contribution_pp : null;
    }
    return contribById.has(fid) ? contribById.get(fid)! : null;
  };
  // Eligible pillars = not withheld. A withheld pillar contributes nothing and cannot be crowned.
  const eligible = haveBridge ? scored.filter((f) => !isWithheld(f.id)) : scored;
  const rankOf = (f: ScoredFactor): number => {
    const c = contribOf(f.id);
    return c != null ? c : (haveBridge ? -Infinity : f.score);
  };
  // Pre-revenue audit P1: "strongest driver" is the strongest MEASURED driver — the SAME rule the
  // Dossier's analytical summary uses (status === 'MEASURED'). Otherwise the page could crown an
  // ESTIMATED pillar (e.g. Security) while the Dossier names the strongest MEASURED one (Regulatory),
  // and the two surfaces disagree. Falls back to the eligible set only if NO pillar is fully measured.
  const isMeasured = (f: ScoredFactor) => (haveBridge ? bridgeOf(f.id)?.status === 'MEASURED' : true);
  const measuredPool = eligible.filter(isMeasured);
  const pool = measuredPool.length ? measuredPool : eligible;
  const usingMeasured = measuredPool.length > 0;
  // ESG is excluded from the "strongest" headline (E-only, 0.05 weight — never the true driver).
  const strongestPool = pool.filter((f) => f.id !== 'esg');
  const localStrongest: Factor = (strongestPool.length ? strongestPool : pool).length
    ? (strongestPool.length ? strongestPool : pool).reduce((max, f) => (rankOf(f) > rankOf(max) ? f : max))
    : EMPTY;
  // P0-30: prefer the ONE authority the backend seals into the bridge (strongest_measured_id) so this
  // page and the Dossier can NEVER name different "strongest measured" drivers (the On-Chain vs
  // Regulatory split). Fall back to the local computation only for older payloads without the field.
  const strongest: Factor =
    (haveBridge && compositeBridge?.strongest_measured_id
      ? scored.find((f) => bridgeOf(f.id)?.id === compositeBridge.strongest_measured_id)
      : undefined) ?? localStrongest;
  // SR-361 (owner 2026-09-26): a Watch Point exists only when an ADVERSE predicate holds — the template never has
  // to fill the negative slot, and the strongest contributor can never also be the watch. Candidates, in order:
  //   1. an eligible (not withheld) pillar with a negative contribution or a score below 60 — the weakest such;
  //   2. current-market risk: the canonical risk state is ELEVATED / HIGH / CRITICAL;
  //   3. an evidence gap: scoreable framework coverage below 100%;
  //   4. none — "No quality-pillar weakness identified".
  // SR-361 / SR-364: the watch point comes from the ONE shared selector (utils/executiveState) — the asset
  // header, this summary and the Orchestrator brief read the same canonical state.
  const ex = executiveState(metrics);
  // SR-368: show the evidence class (CURATED_VERIFIED, OBSERVED …), never the scoring status as if it were one.
  const strongestTier = (haveBridge ? ((bridgeOf(strongest.id) as any)?.evidence_class || bridgeOf(strongest.id)?.status) : undefined) ?? '';
  const BRIDGE_TO_FACTOR: Record<string, string> = { fundamentals: 'fundamentals', onchain: 'on_chain', market: 'market_sentiment', security: 'security', regulatory: 'regulatory', esg: 'esg' };
  const needsAttention: Factor = ex.watch.kind === 'pillar' && ex.watch.id
    ? (scored.find((f) => f.id === BRIDGE_TO_FACTOR[ex.watch.id!]) ?? EMPTY)
    : EMPTY;
  type Watch = { kind: 'pillar' | 'risk' | 'coverage' | 'none'; title: string; value: string; unit: string; note: string };
  const watch: Watch = {
    ...ex.watch,
    note: ex.watch.kind === 'risk' ? `Canonical risk state under risk_state_policy_v2 (current-market drivers only) — ${ex.elevatedCoreDrivers} of ${ex.coreDrivers} core drivers elevated.`
      : ex.watch.kind === 'coverage' ? 'Part of the class framework is not scoreable this run; the composite covers the scoreable share only.'
      : ex.watch.kind === 'none' ? 'No eligible pillar has a negative contribution or a score below 60; current-market risk is not elevated.' : '',
  };
  // Primary drivers list — from the bridge (withheld pillars omitted, renormalized values).
  const primaryDrivers: Array<{ id: string; label: string; value: number }> = haveBridge
    ? bridgePillars.filter((p) => p.status !== 'WITHHELD' && typeof p.contribution_pp === 'number' && Math.abs(p.contribution_pp) >= 0.5).map((p) => ({ id: p.id, label: p.label, value: p.contribution_pp })).sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
    : (institutionalAnalysis?.contribution_pp ?? []).filter((c) => Math.abs(c.value) >= 0.5).map((c) => ({ id: c.id, label: CATEGORY_LABELS[c.id] ?? c.id, value: c.value }));
  // Pre-revenue audit P1: the 30d-volatility "risk-review threshold" was a flat 38% — below any
  // crypto's realised vol, so the flag fired permanently and carried no information. It's now
  // per-asset-class: large-caps (market cap ≥ $50B) 90%, everything smaller 120% (owner-approved).
  // P2 calibration: large-cap (mcap ≥ $50B) 90%; mid-cap 100% (was 120% — most mid-caps sit below
  // 120% realised vol, so that threshold almost never fired; 100% flags a genuinely elevated mid-cap
  // while still sitting above its normal band).
  const mcapB = typeof metrics?.market_cap === 'number' ? metrics.market_cap : null;
  const volThreshold = mcapB != null && mcapB >= 50 ? 90 : 100;
  const volClassLabel = mcapB != null && mcapB >= 50 ? 'large-cap' : 'mid-cap';
  const confRaw = institutionalAnalysis?.composite?.confidence;
  const dqRaw = institutionalAnalysis?.composite?.data_quality;
  const hasConf = confRaw != null && dqRaw != null; // no fabricated 0.78/0.84 — show '—' when absent
  const conf = confRaw ?? 0;
  const dq = dqRaw ?? 0;
  // Prefer backend's confidence_effective + confidence_capped_by_dq (single source of truth from
  // aggregate.go — Dossier View P0.2 backend follow-through 2026-08-22). Fall back to computing
  // locally only when the payload predates that field. Invariant either way: confidence cannot
  // be more certain than the data it rests on.
  const backendConfEffective = institutionalAnalysis?.composite?.confidence_effective;
  // confidence_capped_by_dq is folded into the single Evidence-quality value (SR-147 P1)
  const confEffective = backendConfEffective != null ? backendConfEffective : Math.min(conf, dq);
  // (confidence cap now folded into the single Evidence-quality value — SR-147 P1)
  const lowConfidence = confEffective < 0.6 || dq < 0.7;
  // Single rounding convention for all scores shown on this screen: 1 decimal,
  // matching the composite formula (so the headline reconciles with the math and
  // there is no 94.2-vs-94 / 60-vs-60.0 drift).
  const fmt1 = (n: number | null | undefined) => (n != null && Number.isFinite(Number(n)) ? Number(n).toFixed(1) : 'N/A');

  // Generate dynamic summary based on metrics
  const getAssetName = (symbol: string) => {
    const names: { [key: string]: string } = {
      'btc': 'Bitcoin', 'bitcoin': 'Bitcoin',
      'eth': 'Ethereum', 'ethereum': 'Ethereum',
      'sol': 'Solana', 'solana': 'Solana',
      'link': 'Chainlink', 'chainlink': 'Chainlink',
      'ada': 'Cardano', 'cardano': 'Cardano'
    };
    return names[symbol.toLowerCase()] || symbol.toUpperCase();
  };

  const assetName = getAssetName(asset);

  // Page design language (same as the SAA Quality block, Home.tsx data-testid saa-quality-block): panel tokens for
  // surfaces, --c-text / --c-text-body / --c-text-soft for text, named status chips — amber = limitation /
  // uncertainty, neutral = not a check. Contributions are signed and neutral (no green/red by sign).
  const sectionLabel = 'text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--c-text-soft)]';
  const chipCls = (amber: boolean) => amber
    ? 'border-[color:rgb(var(--c-amber-rgb)/0.35)] bg-[color:rgb(var(--c-amber-rgb)/0.1)] text-[color:var(--c-amber)]'
    : 'border-[color:rgb(var(--c-ink-rgb)/0.14)] bg-transparent text-[color:var(--c-text)]';
  const Chip = ({ label, children, amber = false, title, testId }: { label: string; children: React.ReactNode; amber?: boolean; title?: string; testId?: string }) => (
    <li title={title} data-testid={testId} className={`inline-flex max-w-full items-baseline gap-2 rounded border px-2.5 py-1 ${chipCls(amber)}`}>
      <span className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--c-text-soft)]">{label}</span>
      <span className="min-w-0 break-words font-mono text-[13px]">{children}</span>
    </li>
  );
  const gradeShown = gradeDisplay(grade).replace(/^grade\s+/i, '');
  // A grade STATE (pending recalibration, indeterminate, not assigned) is a limitation; an SAA-n band is not.
  const gradeIsState = !/^SAA-\d/.test(gradeDisplay(grade));
  const covBelowFull = typeof compCovRaw === 'number' && compCovRaw < 0.999;
  const cardCls = 'flex min-w-0 flex-col rounded-md border border-[color:rgb(var(--c-ink-rgb)/0.08)] bg-[color:var(--c-panel-black)] p-4 md:p-5';
  const cardHead = (icon: React.ReactNode, label: string, title?: string) => (
    <div className="mb-3 flex items-center gap-2">
      {icon}
      <span className={sectionLabel} title={title}>{label}</span>
    </div>
  );

  return (
    <section className="py-12 px-4 md:px-10 lg:px-20 bg-[color:var(--c-panel-black)]">
      <div className="max-w-[1600px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          {/* Main Summary Card — same surface as the SAA Quality block */}
          <div data-testid="exec-summary-card" className="overflow-hidden rounded-lg border border-[color:rgb(var(--c-ink-rgb)/0.06)] bg-[color:var(--c-panel-raised)] text-left shadow-2xl shadow-black/30">
            {/* Header strip: title + provenance, then the composite line as named status chips */}
            <div className="space-y-3 border-b border-[color:rgb(var(--c-ink-rgb)/0.08)] px-4 py-4 md:px-8">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-[19px] font-semibold text-[color:var(--c-text)] md:text-[20px]">
                  Executive Summary for {assetName}
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                  <NarrativeProvenanceChip provenance={aiSummaryText && narrativeOrigin === 'LLM_RECONCILED' ? 'llm' : 'deterministic'} />
                  {aiSummaryText && narrativeOrigin === 'LLM_RECONCILED' && narrativeReadback && <NarrativeReadbackBadge readback={narrativeReadback} />}
                </div>
              </div>

              {/* Composite line: score (grade) | coverage | evidence quality */}
              <ul className="flex flex-wrap gap-2" aria-label="Composite status" data-testid="exec-summary-chips">
                {compWithheld ? (
                  <>
                    <Chip label="Quality · withheld" amber>(grade NOT ASSIGNED)</Chip>
                    <Chip label="Framework scoreable coverage" amber title="Σ nominal framework weight of the axes measured this run. Separate axis from evidence quality (SR-280).">{compCovText}</Chip>
                  </>
                ) : (
                  <>
                    <Chip label="Quality"><span className="font-semibold">{fmt1(overallRating)}</span> /100</Chip>
                    <Chip label="Grade" amber={gradeIsState}>{gradeShown}</Chip>
                    {compCovText && compCovText !== '—' && (
                      <Chip label="Scoreable coverage" amber={covBelowFull} title="Share of the class framework's nominal weight that is scoreable this run — a separate axis from evidence quality (SR-280).">{compCovText}</Chip>
                    )}
                  </>
                )}
                {institutionalAnalysis?.composite && (
                  /* SR-147/P1: one deterministic heuristic, not a calibrated probability — shown ONCE as
                     Evidence quality (the former "Confidence" was capped at data quality anyway). */
                  <Chip label="Available-evidence quality" amber={hasConf && confEffective < 0.6} title="Quality of the inputs that ARE present (share directly observed, bounded by data quality) — a deterministic heuristic, NOT a statistical confidence, and NOT coverage: framework coverage is a separate axis (SR-280).">{hasConf ? `${Math.round(confEffective * 100)}/100` : '—'}</Chip>
                )}
                {lowConfidence && (
                  <li className={`inline-flex items-center gap-1.5 rounded border px-2.5 py-1 text-[13px] ${chipCls(true)}`}>
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    Below evidence-quality threshold — indicative only
                  </li>
                )}
              </ul>
            </div>

            <div className="space-y-6 px-4 py-6 md:px-8 md:py-8">
              {/* Narrative (left) | pillar contributions (right) */}
              <div className={`grid grid-cols-1 gap-6 ${!compWithheld && primaryDrivers.length > 0 ? 'lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] lg:gap-10' : ''}`}>
                {/* Summary Text */}
                <div className="min-w-0">
                  {compWithheld ? (
                    <div className="space-y-2 text-[15px] leading-relaxed text-[color:var(--c-text-body)] md:text-[16px]">
                      <p><strong className="font-semibold text-[color:var(--c-text)]">{assetName}</strong>: quality composite <strong className="font-semibold text-[color:var(--c-amber)]">WITHHELD</strong> — scoreable nominal coverage <span className="font-mono">{compCovText}</span> (required minimum {typeof metrics?.scoreable_coverage_floor === 'number' ? `${(metrics.scoreable_coverage_floor * 100).toFixed(0)}%` : '75%'}) under {(compositeBridge as any)?.framework_ref || metrics?.framework_ref || 'the class framework'}.</p>
                      {compFlags.length > 0 && <p className="font-mono text-[13px] text-[color:var(--c-amber)]">{compFlags.join(' · ')}</p>}
                      {(compositeBridge?.pillars ?? []).filter((p: any) => p.status !== 'WITHHELD').length > 0 && (
                        <p className="text-[14px] text-[color:var(--c-text-body)]">Measured pillars (diagnostics, not a composite): {(compositeBridge?.pillars ?? []).filter((p: any) => p.status !== 'WITHHELD').map((p: any) => `${p.label} ${Number(p.score).toFixed(1)}/100 (${p.status})`).join(', ')}.</p>
                      )}
                      <p className="text-[14px] text-[color:var(--c-text-body)]">Overall grade: NOT ASSIGNED — no threshold or distance applies while load-bearing evidence is missing.</p>
                    </div>
                  ) : aiSummaryText ? (
                    <div className="text-[15px] leading-relaxed text-[color:var(--c-text-body)] md:text-[16px]">
                      {aiSummaryText
                        .replace(/\*\*([^*]+)\*\*/g, '$1')
                        .replace(/\*([^*]+)\*/g, '$1')
                        .split(/\. /)
                        .map((sentence, idx, arr) => {
                          const fullSentence = idx < arr.length - 1 ? sentence + '. ' : sentence;
                          return (
                            <span key={idx}>
                              {fullSentence.split(/(\d+\/100|\([A-Z]{1,2}\)|TAO|LEO|AVAX|DOGE|NEAR|ENA|STRK|LDO|Bitcoin|Ethereum|Dogecoin)/).map((part, partIdx) => {
                                if (part.match(/^\d+\/100$/)) {
                                  return <span key={partIdx} className="mx-0.5 font-mono font-semibold text-[color:var(--c-text)]">{part}</span>;
                                }
                                if (part.match(/^\([A-Z]{1,2}\)$/)) {
                                  const grade = part.replace(/[()]/g, '');
                                  return <span key={partIdx} className="ml-1 inline-block rounded border border-[color:rgb(var(--c-ink-rgb)/0.14)] px-1.5 py-0.5 font-mono text-[12px] text-[color:var(--c-text)]">{gradeDisplay(grade)}</span>;
                                }
                                if (part === assetName || part === assetName.toUpperCase() || part.toLowerCase() === assetName.toLowerCase()) {
                                  return <strong key={partIdx} className="font-semibold text-[color:var(--c-text)]">{part}</strong>;
                                }
                                return <span key={partIdx}>{part}</span>;
                              })}
                            </span>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="text-[15px] leading-relaxed text-[color:var(--c-text-body)] md:text-[16px]">
                      {institutionalAnalysis ? (
                        <>
                          <strong className="font-semibold text-[color:var(--c-text)]">{assetName}</strong>: Quality <span className="font-mono text-[color:var(--c-text)]">{fmt1(overallRating)}</span>/100 · grade <strong className="font-semibold text-[color:var(--c-text)]">{gradeShown}</strong>. Strongest evidence-eligible contributor is{' '}
                          <strong className="font-semibold text-[color:var(--c-text)]">{strongest.name}</strong> ({fmt1(strongest.score)}/100
                          {contribOf(strongest.id) != null && (
                            <>, +{contribOf(strongest.id)!.toFixed(2)} pts contribution</>
                          )}
                          {strongestTier && <>, {strongestTier.replace(/_/g, ' ')}</>}
                          ).{' '}
                          {watch.kind === 'pillar' && (<>Watch point: <strong className="font-semibold text-[color:var(--c-amber)]">{watch.title}</strong> ({watch.value}/100{contribOf(needsAttention.id) != null && <>, {contribOf(needsAttention.id)! >= 0 ? '+' : ''}{contribOf(needsAttention.id)!.toFixed(2)} pts</>}).</>)}
                          {watch.kind === 'risk' && (<>Watch point: <strong className="font-semibold text-[color:var(--c-amber)]">{watch.title} {watch.value}</strong> {watch.unit}.</>)}
                          {watch.kind === 'coverage' && (<>Watch point: scoreable framework coverage <strong className="font-semibold text-[color:var(--c-amber)]">{watch.value}</strong>.</>)}
                          {watch.kind === 'none' && (<>{watch.title}.</>)}
                          {/* P0-30: PRINT the exclusion rule — why a larger raw contributor is not the named
                              measured driver — so the claim is self-explaining and matches the Dossier. */}
                          {usingMeasured && (compositeBridge?.measured_exclusions?.length ?? 0) > 0 && (
                            <span className="mt-3 block text-[13px] text-[color:var(--c-text-soft)]">
                              Larger raw contributors are not evidence-eligible (scoring tier below MEASURED):{' '}
                              {compositeBridge!.measured_exclusions!.map((e, i) => (
                                <span key={e.id}>{i > 0 ? ', ' : ''}{e.label} (+{e.contribution_pp.toFixed(2)} pts, {e.status})</span>
                              ))}.
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          <span>
                            {assetName} maintains{' '}
                            {grade === 'PENDING_RECALIBRATION' ? (
                              <strong className="font-semibold text-[color:var(--c-text)]">a binding quality score (grade pending recalibration under Market Quality v2)</strong>
                            ) : overallRating != null && overallRating >= 85 ? (
                              <strong className="font-semibold text-[color:var(--c-text)]">exceptional institutional-grade quality</strong>
                            ) : overallRating != null && overallRating >= 75 ? (
                              <strong className="font-semibold text-[color:var(--c-text)]">solid institutional-grade status</strong>
                            ) : (
                              <strong className="font-semibold text-[color:var(--c-text)]">institutional characteristics</strong>
                            )}
                            {' '}with a composite score of{' '}
                            <span className="mx-1 font-mono text-[18px] font-semibold text-[color:var(--c-text)]">{fmt1(overallRating)}/100</span>
                            <span className={`ml-1 inline-block rounded border px-1.5 py-0.5 font-mono text-[12px] ${chipCls(gradeIsState)}`}>{gradeDisplay(grade)}</span>.
                          </span>
                          {' '}
                          {onChainScore != null && onChainScore >= 85 && metrics.active_addresses > 100000 && (
                            <span>
                              Strong on-chain activity with{' '}
                              <strong className="font-semibold text-[color:var(--c-text)]">
                                {Math.round(metrics.active_addresses / 1000)}K active addresses
                              </strong>{' '}
                              driving network adoption.{' '}
                            </span>
                          )}
                          {needsAttention.score != null && needsAttention.score < 80 && (
                            <span>
                              Primary watch point: <strong className="font-semibold text-[color:var(--c-amber)]">{needsAttention.name}</strong>{' '}
                              ({fmt1(needsAttention.score)}/100) requires ongoing monitoring.
                            </span>
                          )}
                          {needsAttention.score != null && needsAttention.score >= 80 && (
                            <span>
                              No pillar scores below 80 on this run; see the pillar breakdown for the evidence tier of each.
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Primary Drivers (contribution in pp) — from the composite bridge (renormalized,
                    withheld pillars omitted), so the summary and the bridge table can never disagree. */}
                {!compWithheld && primaryDrivers.length > 0 && (
                  <div className="min-w-0" data-testid="exec-summary-contributions">
                    {/* Pre-revenue audit P2: this list filters to |contribution| ≥ 0.5pp, so it omits
                        near-neutral pillars (e.g. Market +0.22pp). Labelled "Top drivers" so it never
                        reads as the FULL set — the complete per-pillar breakdown is the composite bridge below. */}
                    <h3 className={`mb-2 ${sectionLabel}`} title="Signed contribution of each pillar to the Quality composite (score − 50 × normalized weight; withheld pillars dropped, the rest renormalized); |contribution| ≥ 0.5 pts shown — positive support and negative drag alike. Full breakdown: the composite bridge below.">Pillar contributions</h3>
                    <ul className="rounded-md border border-[color:rgb(var(--c-ink-rgb)/0.08)] bg-[color:var(--c-panel-black)] px-4 py-1">
                      {primaryDrivers.map((c) => (
                        <li key={c.id} className="flex items-baseline justify-between gap-4 border-b border-[color:rgb(var(--c-ink-rgb)/0.06)] py-2 last:border-b-0">
                          <span className="min-w-0 text-[14px] text-[color:var(--c-text-body)]">{c.label}</span>
                          <span className="shrink-0 font-mono text-[14px] tabular-nums text-[color:var(--c-text)]">{c.value >= 0 ? '+' : ''}{c.value.toFixed(2)} pts</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Key Insights Grid */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {/* Strongest Factor — owner ATOM review 2026-09-29: a withheld composite names no supporting contributor. */}
                {compWithheld ? (
                <div className={cardCls}>
                  {cardHead(<TrendingUp className="h-4 w-4 shrink-0 text-[color:var(--c-text-soft)]" />, 'PILLAR STRENGTH · NOT CONCLUDED')}
                  <p className="text-[14px] text-[color:var(--c-text-body)]">{WITHHELD_PILLAR_STRENGTH} Diagnostic pillar scores are in the breakdown below.</p>
                </div>
                ) : (
                <div className={cardCls}>
                  {cardHead(<TrendingUp className="h-4 w-4 shrink-0 text-[color:var(--c-text-soft)]" />, 'STRONGEST SUPPORTED CONTRIBUTOR', 'The strongest evidence-eligible pillar by contribution to the composite (the composite bridge\'s selector — same rule as the Dossier). Its evidence tier is shown beside the name.')}
                  <h4 className="mb-2 text-[16px] font-semibold text-[color:var(--c-text)] md:text-[17px]">{strongest.name}{strongestTier && <span className="ml-2 align-middle rounded border border-[color:rgb(var(--c-ink-rgb)/0.14)] px-1.5 py-0.5 font-mono text-[11px] font-normal tracking-wide text-[color:var(--c-text)]">{strongestTier.replace(/_/g, ' ')}</span>}</h4>
                  <div className="mb-2 flex items-baseline gap-2">
                    <span className="font-mono text-[28px] font-semibold leading-none text-[color:var(--c-text)]">{fmt1(strongest.score)}</span>
                    <span className="font-mono text-[14px] text-[color:var(--c-text-soft)]">/100</span>
                  </div>
                  <p className="text-[14px] text-[color:var(--c-text-body)]">
                    {/* Pre-revenue audit P0-11: never print a sub-metric that is WITHHELD as a zero
                        (the "0.0B daily volume" / phantom "0 days without incidents" false zeros).
                        Show the measured detail only when it exists; else fall back to the pillar score. */}
                    {strongest.id === 'on_chain' && (typeof metrics.transaction_volume === 'number' && metrics.transaction_volume > 0
                      ? `${metrics.transaction_volume.toFixed(1)}B daily volume`
                      : `${fmt1(strongest.score)}/100 on-chain activity`)}
                    {strongest.id === 'fundamentals' && `Development activity ${strongest.change}`}
                    {strongest.id === 'security' && (typeof metrics.days_since_incident === 'number' && metrics.days_since_incident >= 0
                      ? <span title="Incident basis = the SAA curated incident ledger (verified, primary-sourced, own security boundary). Days are derived at build time from the verified incident date, or — for a COMPLETE review with no S2+ established — from the review start. With no verified basis the counter is withheld, never shown as clean.">{metrics.incident_verification === 'CURATED_VERIFIED_CLEAN_WINDOW' ? `${metrics.days_since_incident} days counted from incident-review start · no S2+ established` : `${metrics.days_since_incident} days since verified incident`}</span>
                      : <span>{fmt1(strongest.score)}/100 security posture</span>)}
                    {strongest.id !== 'on_chain' && strongest.id !== 'fundamentals' && strongest.id !== 'security' && strongest.change}
                  </p>
                </div>
                )}

                {/* Watch Point — amber marks the limitation (the adverse predicate), not the whole card */}
                <div className={cardCls}>
                  {cardHead(<AlertTriangle className={`h-4 w-4 shrink-0 ${watch.kind === 'none' ? 'text-[color:var(--c-text-soft)]' : 'text-[color:var(--c-amber)]'}`} />, 'WATCH POINT')}
                  <h4 className="mb-2 text-[16px] font-semibold text-[color:var(--c-text)] md:text-[17px]">{watch.title}</h4>
                  {watch.kind !== 'none' && (
                    <div className="mb-2 flex items-baseline gap-2">
                      <span className="font-mono text-[28px] font-semibold leading-none text-[color:var(--c-amber)]">{watch.value}</span>
                      <span className="font-mono text-[14px] text-[color:var(--c-text-soft)]">{watch.unit}</span>
                    </div>
                  )}
                  <p className="text-[14px] text-[color:var(--c-text-body)]">
                    {watch.kind === 'pillar'
                      ? (needsAttention.id === 'market_sentiment'
                      ? (() => {
                          // Render the ACTUAL Market sub-components published by the core (single source),
                          // so the reader can reconcile the score to the cent — value × weight = contribution
                          // for each term, with the excluded correlation named (SELF_REFERENCE for the root
                          // asset, DATA_SUSPECT for an implausible correlation) and weights renormalized.
                          const bd: any = (metrics as any).market_score_breakdown;
                          if (bd && Array.isArray(bd.components)) {
                            // v1 (non-migrated classes) and market_quality_v2 (SR-198, NATIVE_L1) component ids.
                            const nm = (n: string) => ({
                              volatility_inverse: 'vol (inverse)', sentiment: 'sentiment', btc_decoupling: 'BTC correlation',
                              stability_vol_90d: 'stability · 90D vol', liquidity_capacity_abs: 'executable capacity ≤50 bps',
                              liquidity_capacity_rel: 'relative capacity', venue_diversification: 'venue diversification', derivatives_stability: 'derivatives stability',
                            } as Record<string, string>)[n] || n;
                            const included = bd.components.filter((c: any) => !c.excluded);
                            const excluded = bd.components.filter((c: any) => c.excluded);
                            const parts = included.map((c: any) => `${nm(c.name)} ${Number(c.value).toFixed(1)}/100 × ${Math.round(Number(c.weight) * 100)}% = ${Number(c.contribution).toFixed(2)} pts`);
                            let out = parts.length
                              ? `Market ${Number(bd.score).toFixed(1)} = ${parts.join(' + ')}`
                              : `Market pillar withheld — no measurement-grade sub-input this run`;
                            // Only explain the vol term when volatility is actually IN the score (measured).
                            // A WITHHELD/prior volatility is excluded below, never printed as "100 − 0.0%".
                            const volIncluded = included.some((c: any) => c.name === 'volatility_inverse' || c.name === 'realized_volatility');
                            if (volIncluded) {
                              const vol = institutionalAnalysis?.risk_panel?.realized_vol_30d_pct ?? (typeof metrics.volatility_30d === 'number' ? metrics.volatility_30d : null);
                              out += `. Vol (inverse) = 100 − 30d realized vol${vol != null ? ` (${vol.toFixed(1)}%${vol > volThreshold ? ` — above the ${volThreshold}% ${volClassLabel} flag` : ''})` : ''}`;
                            }
                            if (excluded.length) {
                              const label = excluded.map((c: any) => `${nm(c.name)} (${c.exclude_reason})`).join(', ');
                              out += `. Excluded — ${label}; surviving weights renormalized to 100%`;
                            }
                            return out + '.';
                          }
                          // Fallback (breakdown not yet in payload): raw inputs only.
                          const vol = institutionalAnalysis?.risk_panel?.realized_vol_30d_pct ?? (typeof metrics.volatility_30d === 'number' ? metrics.volatility_30d : null);
                          const s = typeof metrics.sentiment_index === 'number' ? metrics.sentiment_index : null;
                          const sent100 = s == null ? null : (s >= -1 && s <= 1 ? Math.round((s + 1) * 50) : Math.max(0, Math.min(100, Math.round(s))));
                          const corr = typeof metrics.btc_correlation === 'number' ? metrics.btc_correlation : null;
                          const parts: string[] = [];
                          if (sent100 != null) parts.push(`sentiment ${sent100}/100 (45% wt)`);
                          if (corr != null && Math.abs(corr) >= 0.30) parts.push(`BTC corr ${corr.toFixed(2)} (25% wt)`);
                          if (vol != null) parts.push(`30d vol ${vol.toFixed(1)}% (inverse, 30% wt${vol > volThreshold ? ` — above the ${volThreshold}% ${volClassLabel} flag` : ''})`);
                          return parts.length ? `Driven by ${parts.join(' · ')}.` : `Pillar ${fmt1(needsAttention.score)}/100 — see the Market card for the sub-component breakdown.`;
                        })()
                      : `Adverse predicate met: ${(contribOf(needsAttention.id) ?? 0) < 0 ? `negative contribution (${contribOf(needsAttention.id)!.toFixed(2)} pts)` : 'score below 60'}; see the pillar dossier.`)
                      : null}
                    {watch.kind !== 'pillar' && watch.note}
                  </p>
                </div>

                {/* Context: volatility + trigger when institutional; else generic bullets */}
                <div className={cardCls}>
                  {cardHead(<Info className="h-4 w-4 shrink-0 text-[color:var(--c-text-soft)]" />, 'MARKET CONTEXT')}
                  <h4 className="mb-2 text-[16px] font-semibold text-[color:var(--c-text)] md:text-[17px]">Recent Activity</h4>
                  <div className="space-y-2">
                    {/* realized_vol_30d_pct follows the backend "0 = insufficient/N/A" contract (never a real
                        0% vol) — so treat <= 0 as WITHHELD, exactly like the report path, instead of printing
                        "Volatility 0.0% (30d)". When it is withheld we say so honestly rather than showing
                        fabricated activity bullets. */}
                    {institutionalAnalysis?.risk_panel?.realized_vol_30d_pct != null && institutionalAnalysis.risk_panel.realized_vol_30d_pct > 0 ? (
                      <p className="text-[14px] text-[color:var(--c-text-body)]">
                        Volatility <span className="font-mono text-[color:var(--c-text)]">{institutionalAnalysis.risk_panel.realized_vol_30d_pct.toFixed(1)}%</span> (30d).
                        {institutionalAnalysis.risk_panel.realized_vol_30d_pct > volThreshold ? (
                          <span className="text-[color:var(--c-amber)]" title={`The ${volThreshold}% ${volClassLabel} threshold is a monitoring flag for risk review; it is not wired to an automatic score penalty. Threshold is set by asset class (large-cap ≥$50B: 90%, mid-cap: 120%).`}> Above the {volThreshold}% {volClassLabel} risk-review threshold — flagged.</span>
                        ) : institutionalAnalysis.risk_panel.realized_vol_30d_pct > volThreshold - 5 ? (
                          <span className="text-[color:var(--c-text-soft)]"> Approaching the {volThreshold}% {volClassLabel} risk-review threshold.</span>
                        ) : null}
                      </p>
                    ) : (
                      <p className="text-[14px] text-[color:var(--c-text-body)]">
                        Realized volatility (30d) withheld — insufficient price history this run (price feed rate-limited or too few daily closes). It returns once the OHLC series is available.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {afterSummary}
            </div>

            {/* "View Detailed Analysis" moved to the header (right of Portfolio) — it opens /analysis/:asset as a
                standalone page in its own window (owner 2026-09-24). */}

            {/* Provenance footer when institutional analysis present. Confidence here MUST be the
                effective (data-quality-capped) value — the same object the header renders — so a
                single panel never shows two confidences (Dossier View panel review 2026-08-22:
                header 0.83 vs footer 0.93 was exactly this class of bug). Raw model confidence
                stays accessible via the header tooltip when the cap fires, but never as a second
                headline number. */}
            <div className="space-y-1 border-t border-[color:rgb(var(--c-ink-rgb)/0.08)] px-4 py-4 font-mono text-[11px] leading-relaxed text-[color:var(--c-text-soft)] md:px-8">
              {institutionalAnalysis?.provenance && (
                <p>
                  Model v{institutionalAnalysis.provenance.model_version}
                  {' · '}Snapshot {institutionalAnalysis.provenance.feature_store_snapshot}
                  {' · '}Framework scoreable coverage {compCovText}
                  {' · '}Available-evidence quality {hasConf ? `${Math.round(confEffective * 100)}/100` : '—'} (deterministic heuristic, not a calibrated probability).
                </p>
              )}
              {/* Disclaimer */}
              <p>
                {/* SR-147: wording rendered ONLY from the canonical narrative_origin enum. */}
                {narrativeOrigin === 'LLM_RECONCILED' && aiSummaryText
                  ? 'Narrative origin: LLM · metrics locked by the canonical core · reconciliation PASS. All figures are computed by the analytics core, not the model.'
                  : narrativeOrigin === 'LLM_SUPPRESSED'
                    ? 'Narrative origin: deterministic template fallback — the LLM candidate was suppressed after failing reconciliation. All figures are computed by the analytics core.'
                    : 'Narrative origin: deterministic template — composed from canonical-core metrics; no LLM prose shown.'}
                {!institutionalAnalysis?.provenance && (
                  <> Last updated: {new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</>
                )}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Composite bridge table (Executive Summary panel P0.1) — the ONE decomposition every
            narrative selector must derive from. Renders immediately after the summary card so a
            reader can cross-check any "weakest / strongest / primary-watch" claim against argmin/
            argmax of contribution_pp. */}
        {compositeBridge && (
          <div className="mt-4">
            <CompositeBridgePanel bridge={compositeBridge} />
          </div>
        )}

        {/* Typed metric registry (MVP slice, panel-4 deferred foundation). The ONE authoritative
            per-(key,window) object every number-bearing surface must read; renders here as a
            table so a reader can watch which values are MEASURED vs WITHHELD at a glance. */}
        {typedMetricRegistry && (
          <div className="mt-4">
            <TypedMetricRegistryPanel registry={typedMetricRegistry} />
          </div>
        )}

        {/* Evidence passport footer (contract-pack §5) — as-of + canonical_status_sha256 + policy_version + expand. */}
        {evidencePassport && (
          <div className="mt-4">
            <EvidencePassportFooter passport={evidencePassport} />
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-6">
          <Disclaimer variant="compact" />
        </div>
      </div>
    </section>
  );
};

export default AISummary;

